import { useState } from "react";
import { Check, ChevronsUpDown, MapPin } from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";

// Lista completa dei Comuni del Canton Ticino per distretto
const COMUNI_TICINO = {
  "Frontalieri": [
    "Residente in Italia (Frontaliere)"
  ],
  "Distretto di Lugano": [
    "Agno", "Alto Malcantone", "Aranno", "Arogno", "Astano", "Bedano", "Bedigliora", 
    "Bioggio", "Bissone", "Brusino Arsizio", "Cademario", "Cadempino", "Canobbio", 
    "Capriasca", "Caslano", "Collina d'Oro", "Comano", "Curio", "Grancia", "Gravesano", 
    "Lamone", "Lugano", "Magliaso", "Manno", "Massagno", "Mezzovico-Vira", 
    "Miglieglia", "Monteceneri", "Morcote", "Muzzano", "Neggio", "Novaggio", "Origlio", 
    "Paradiso", "Ponte Capriasca", "Pura", "Savosa", "Sorengo", 
    "Torricella-Taverne", "Tresa", "Vico Morcote", "Vezia"
  ],
  "Distretto di Bellinzona": [
    "Bellinzona", "Arbedo-Castione", "Cadenazzo", "Isone", "Lumino", "Sant'Antonino"
  ],
  "Distretto di Locarno": [
    "Ascona", "Brione sopra Minusio", "Brissago", "Centovalli", "Gambarogno", "Gordola", 
    "Intragna", "Lavertezzo", "Locarno", "Losone", "Mergoscia", "Minusio", "Muralto", 
    "Onsernone", "Orselina", "Ronco sopra Ascona", "Tenero-Contra", "Terre di Pedemonte", "Verzasca"
  ],
  "Distretto di Mendrisio": [
    "Balerna", "Castel San Pietro", "Chiasso", "Coldrerio", "Mendrisio", 
    "Morbio Inferiore", "Novazzano", "Riva San Vitale", "Stabio", "Vacallo"
  ],
  "Distretto di Riviera": [
    "Biasca", "Riviera"
  ],
  "Distretto di Leventina": [
    "Airolo", "Bedretto", "Bodio", "Dalpe", "Faido", "Giornico", 
    "Personico", "Pollegio", "Prato Leventina", "Quinto"
  ],
  "Distretto di Blenio": [
    "Acquarossa", "Blenio", "Serravalle"
  ],
  "Distretto di Vallemaggia": [
    "Avegno Gordevio", "Bosco Gurin", "Campo (Vallemaggia)", "Cerentino", 
    "Cevio", "Lavizzara", "Linescio", "Maggia"
  ]
};

export function ComuniCombobox({ value, onValueChange, required = false, className }) {
  const [open, setOpen] = useState(false);

  const handleSelect = (currentValue) => {
    // cmdk returns lowercase value, find the original case
    const allComuni = Object.values(COMUNI_TICINO).flat();
    const selectedComune = allComuni.find(c => c.toLowerCase() === currentValue.toLowerCase());
    
    if (selectedComune) {
      onValueChange(selectedComune === value ? "" : selectedComune);
    }
    setOpen(false);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className={cn(
            "w-full justify-between h-11 font-normal bg-white border-slate-300 text-black hover:bg-slate-50",
            !value && "text-slate-500",
            className
          )}
          data-testid="comune-combobox"
        >
          <div className="flex items-center gap-2">
            <MapPin className="h-4 w-4 text-slate-400" />
            {value || "Seleziona il tuo comune..."}
          </div>
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[--radix-popover-trigger-width] p-0 bg-white" align="start" style={{ pointerEvents: 'auto' }}>
        <Command>
          <CommandInput placeholder="Cerca comune..." className="bg-white text-black" />
          <CommandList className="bg-white">
            <CommandEmpty>Nessun comune trovato.</CommandEmpty>
            {Object.entries(COMUNI_TICINO).map(([distretto, comuni]) => (
              <CommandGroup key={distretto} heading={distretto}>
                {comuni.map((comune) => (
                  <CommandItem
                    key={comune}
                    value={comune}
                    onSelect={handleSelect}
                    className="cursor-pointer text-black hover:bg-slate-100"
                    style={{ pointerEvents: 'auto' }}
                  >
                    <Check
                      className={cn(
                        "mr-2 h-4 w-4",
                        value === comune ? "opacity-100" : "opacity-0"
                      )}
                    />
                    {comune}
                  </CommandItem>
                ))}
              </CommandGroup>
            ))}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

export default ComuniCombobox;
